import { ChartRef } from '@/components/ECharts/useChart';
import { FullScreenProps } from '@/components/FullScreen';
import { WithLoader } from '@/components/WithLoader';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { createVariableNameMap } from '@/dashboards/utils/createVariableMap';
import { Typography } from '@analytical-alley/ui';
import React, { useMemo, useRef } from 'react';
import { ROICurvesChart, ROICurvesChartProps } from './ROICurvesChart';

export const ROICurvesBase = ({ fullScreenButton }: FullScreenProps) => {
    const chartRef = useRef<ChartRef>(null);
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const {
        isLoading,
        isFetching,
        data: { previous, initialAllocation, optimized },
    } = useBudgetOptimizationContext();

    const {
        dashboard: { dashboardVariables },
    } = useDashboardGlobalContext();

    const variableMap = useMemo(
        () =>
            createVariableNameMap(
                dashboardVariables.filter((v) => v.fileType === 'output'),
            ),
        [dashboardVariables],
    );

    const points: ROICurvesChartProps['points'] = useMemo(() => {
        return {
            previousAllocation: initialAllocation,
            optimalAllocation: optimized?.optimalVariables,
        };
    }, [initialAllocation, optimized?.optimalVariables]);

    return (
        <div className="glass-tile p-6 flex flex-col gap-4">
            <WithLoader
                isLoading={isLoading}
                isFetching={isFetching}
                loadingMessage={false}
                delayedMessage={false}
            >
                <div className="flex items-center">
                    <Typography
                        variant="titleS"
                        tooltip="Track the return as the spends increase for each channel. Compare previous versus optimal point on the chart."
                    >
                        ROI Curves and simulated investments
                    </Typography>
                    <span className="grow" />
                    <div className="mx-2" ref={filterContainerRef} />
                    {fullScreenButton}
                </div>

                {previous ? (
                    <ROICurvesChart
                        filterContainerRef={filterContainerRef}
                        chartRef={chartRef}
                        data={previous.roiCurves}
                        variableMap={variableMap}
                        points={points}
                        investmentStep={optimized?.investmentStep || 100}
                    />
                ) : (
                    <div className={isFetching ? 'opacity-50' : undefined}>
                        <div className="flex items-center justify-center w-full h-[24rem]" />
                    </div>
                )}
            </WithLoader>
        </div>
    );
};
